import WarningIcon from '@mui/icons-material/Warning';
import { Avatar, Box, Grid, ListItemButton, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useClientTheme } from '../../helpers/hooks/useClientTheme';
import {
  absDifference,
  applyOffsetToTimestamp,
} from '../../helpers/utilityFunctions/utilityFunctions';
import { Alert } from '../../model/passive/alertMoment';
import { AlertPressureType } from '../../model/passive/alertPressureType';
import { PassiveParameterType } from '../../model/passive/passiveParameterType';

interface Props {
  type: string;
  data: Alert;
  index: any;
  selected: boolean;
  onClick: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => void;
}

const SustainedMinutes: Partial<Record<AlertPressureType, number>> = {
  [AlertPressureType.constant15min]: 15,
  [AlertPressureType.constant30min]: 30,
  [AlertPressureType.constant45min]: 45,
  [AlertPressureType.constant60min]: 60,
};

const ParseSensorData = (sensorData: string) =>
  JSON.parse(sensorData || '[]') as number[];

// A foot is affected when its pressure array has any non-zero sensor value.
const AffectedFeet = (data: Alert) =>
  [
    { side: 'left' as const, values: ParseSensorData(data.sensorDataLeft) },
    { side: 'right' as const, values: ParseSensorData(data.sensorDataRight) },
  ].filter(({ values }) => values.some((value) => value !== 0));

// Sensor values are in bars (1 bar = 100 kPa).
const PeakKpa = (values: number[]) => Math.round(Math.max(...values) * 100);

const RenderPressureDetails = (t: TFunction, data: Alert) => {
  const affectedFeet = AffectedFeet(data);

  if (
    data.pressureAlertTypeId === AlertPressureType.peakAllFoot ||
    data.pressureAlertTypeId === AlertPressureType.peakFrontFoot
  ) {
    return (
      <>
        <Typography>
          <Trans
            t={t}
            i18nKey="moment.peakPressure"
            components={{ strong: <strong /> }}
          />
        </Typography>
        {affectedFeet.length > 0 && (
          <Typography>
            {affectedFeet.map(({ side, values }) => (
              <Box component="span" key={side} sx={{ mr: 2 }}>
                <Trans
                  t={t}
                  i18nKey={`moment.peakValue.${side}`}
                  values={{ value: PeakKpa(values) }}
                  components={{ strong: <strong /> }}
                />
              </Box>
            ))}
          </Typography>
        )}
      </>
    );
  }

  const foot =
    affectedFeet.length === 2 ? 'both' : (affectedFeet[0]?.side ?? 'unknown');
  const minutes = data.pressureAlertTypeId
    ? SustainedMinutes[data.pressureAlertTypeId as AlertPressureType]
    : undefined;

  return minutes ? (
    <Typography>
      <Trans
        t={t}
        i18nKey={`moment.sustainedPressure.${foot}`}
        values={{ minutes }}
        components={{ strong: <strong /> }}
      />
    </Typography>
  ) : null;
};

// The list labels each day, so the card only shows the time, followed by the
// timezone when it differs from the viewer's (e.g. "10:00:00 GMT+01:00").
const AlertTime = (data: Alert) =>
  applyOffsetToTimestamp(data.dateTime, data.utcTimeZone)
    .split(' ')
    .slice(4)
    .join(' ')
    .trim();

const RenderTypeDetails = (
  t: TFunction,
  type: string,
  selected: boolean,
  data: Alert,
  metrics: any = undefined
) => {
  switch (type) {
    case PassiveParameterType.AlarmPressure:
      return (
        <>
          {RenderPressureDetails(t, data)}
          <Typography>
            Level <strong>{data.alertLevel}</strong>
          </Typography>
        </>
      );
    case PassiveParameterType.AlarmTemperature:
      return (
        <>
          <Typography>
            Temperature Difference:{' '}
            <strong>
              {absDifference(
                JSON.parse(data.sensorDataLeft),
                JSON.parse(data.sensorDataRight)
              )}
              {metrics && metrics.temperature.symbol}
            </strong>
          </Typography>
        </>
      );
    default:
      break;
  }
};

const MomentCard: React.FC<Props> = ({
  type,
  data,
  index,
  selected,
  onClick,
}) => {
  const { theme } = useClientTheme();
  const { t } = useTranslation('cards');

  const RenderAlertColor = (alertLevel: number) => {
    switch (alertLevel) {
      case 1:
        return theme.palette.alertLevel1.main;
      case 2:
        return theme.palette.alertLevel2.main;
      case 3:
        return theme.palette.alertLevel3.main;
      default:
        return theme.palette.alertLevel3.main;
    }
  };

  return (
    <ListItemButton
      key={index}
      selected={selected}
      onClick={(event) => onClick(event, index)}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        padding: '4px 16px 4px 2px',
        borderRadius: '10px',
        border: `2px solid ${
          selected
            ? `rgba(255, 255, 255, 1)`
            : RenderAlertColor(data.alertLevel)
        }`,
        '&.Mui-selected': {
          backgroundColor: RenderAlertColor(data.alertLevel),
          border: `2px solid ${RenderAlertColor(data.alertLevel)}`,

          '& .MuiListItemIcon-root': {},
        },
        '&.Mui-selected:hover': {
          backgroundColor: RenderAlertColor(data.alertLevel),
          border: `2px solid ${RenderAlertColor(data.alertLevel)}`,

          '& .MuiListItemIcon-root': {},
        },
        '&:hover': {
          backgroundColor: theme.palette.grey[300],

          '& .MuiListItemIcon-root': {},
        },
      }}
    >
      <Grid
        container
        direction="row"
        sx={{
          // Fill the card so the icon column sits at the same place on every card
          flexGrow: 1,
          alignItems: 'center',
          borderRadius: '8px 0px 0px 8px',
          borderLeft: `10px solid ${
            selected
              ? `rgba(255, 255, 255, 1)`
              : RenderAlertColor(data.alertLevel)
          }`,
        }}
      >
        <Grid size={1}></Grid>
        <Grid size="grow">
          <Box sx={{ ml: 2, mt: 1, mb: 1 }}>
            {RenderTypeDetails(t, type, selected, data, theme.metrics)}

            <Typography variant="body1">{AlertTime(data)}</Typography>
          </Box>
        </Grid>
        <Grid size="auto">
          <Avatar
            sx={{
              backgroundColor: (theme) => theme.palette.common.white,
              color: (theme) => theme.palette.info.main,
            }}
          >
            <WarningIcon
              sx={{
                color: RenderAlertColor(data.alertLevel),
              }}
            />
          </Avatar>
        </Grid>
      </Grid>
    </ListItemButton>
  );
};

export default MomentCard;
